/**
 * SystemModuleSettingsPage — F12.M Etap C.2.
 *
 * Hub konfiguracyjny per moduł systemowy (analog `ModuleSettingsPage` dla
 * modułów niestandardowych, ale prostszy — bez Kanban/Filters/Webhooks
 * w F12.M; te wracają w późniejszych fazach jeśli będą potrzebne dla
 * modułów systemowych).
 *
 * Route: `/system-modules/:slug/settings?tab=<id>` (tenant scope).
 *
 * Zakładki:
 *  - fields     → editor pól (core + ext)
 *  - layout     → inline builder formularza (SystemModuleBuilderPage embeddedMode)
 *  - list       → inline kreator widoku listy (SystemModuleListBuilderPage embeddedMode)
 *  - numbering  → placeholder F12.I
 *
 * Zarządzanie osią „Kategoria" (`record_categories`) NIE jest już zakładką tego huba —
 * przeniesione do wspólnego panelu ustawień „Kategorie" (`RecordCategoriesSettingsTab`,
 * `/settings?tab=record-categories`), jedno miejsce per kartoteka (decyzja właściciela
 * 2026-08-27, patrz ADR).
 */

import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, FileText, Hash, Layout, type LucideIcon, Table2 } from 'lucide-react';

import { renderIconByName } from '@/components/modules/IconPicker';
import { TabPlaceholder } from '@/components/modules/TabPlaceholder';
import { Spinner } from '@/components/Spinner';
import {
  type SystemModuleField,
  SystemModuleFieldsPanel,
} from '@/components/system-modules/SystemModuleFieldsPanel';
import { api } from '@/lib/api';

import { SystemModuleBuilderPage } from './SystemModuleBuilderPage';
import { SystemModuleListBuilderPage } from './SystemModuleListBuilderPage';

interface SystemModuleDetail {
  module: {
    slug: string;
    name: string;
    entityTableName: string;
    extTableName: string;
    icon: string;
    color: string;
    isSystemLocked: boolean;
  };
  fields: SystemModuleField[];
  layouts: Array<{
    id: string;
    name: string;
    layoutType: string;
    isDefault: boolean;
    categoryId: string | null;
    roleIds: string[];
  }>;
  categories: Array<{
    id: string;
    name: string;
    color: string | null;
    icon: string | null;
  }>;
}

type TabId = 'fields' | 'layout' | 'list' | 'numbering';

interface TabDef {
  id: TabId;
  labelKey: string;
  icon: LucideIcon;
}

const BASE_TABS: TabDef[] = [
  { id: 'fields', labelKey: 'tenant.systemModuleSettings.tabs.fields', icon: FileText },
  { id: 'layout', labelKey: 'tenant.systemModuleSettings.tabs.layout', icon: Layout },
  { id: 'list', labelKey: 'tenant.systemModuleSettings.tabs.list', icon: Table2 },
  { id: 'numbering', labelKey: 'tenant.systemModuleSettings.tabs.numbering', icon: Hash },
];

function isValidTab(value: string | null, validTabs: TabDef[]): value is TabId {
  return !!value && validTabs.some((tab) => tab.id === value);
}

export function SystemModuleSettingsPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const tabs = BASE_TABS;
  const requested = params.get('tab');
  const activeTab: TabId = isValidTab(requested, tabs) ? requested : 'fields';

  const detailQuery = useQuery({
    queryKey: ['system-module', slug, 'detail'],
    queryFn: async () => {
      const res = await api.get<SystemModuleDetail>(`/system-modules/${slug}`);
      return res.data;
    },
    // Nie fetchuj, gdy wejście na stary `?tab=categories` i tak zaraz przekieruje (niżej).
    enabled: !!slug && requested !== 'categories',
  });

  const setTab = (next: TabId) => {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    setParams(p, { replace: true });
  };

  if (!slug) {
    return (
      <main className="p-6 text-error-500">{t('tenant.systemModuleSettings.missingSlug')}</main>
    );
  }

  // BC: stary URL zakładki „Kategorie" (`?tab=categories`) — zarządzanie osią „Kategoria"
  // przeniesione do wspólnego panelu ustawień. Łapie też przycisk „Edytuj drzewko kategorii"
  // z buildera layoutu (tab typu `tree_picker`) i ewentualne zakładki/deep-linki.
  if (requested === 'categories') {
    return <Navigate to="/settings/record-categories" replace />;
  }

  if (detailQuery.isLoading) {
    return (
      <main className="flex min-h-full items-center justify-center bg-bg-secondary p-6">
        <Spinner />
      </main>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <main className="p-6">
        <p className="text-error-500" data-testid="system-module-settings-error">
          {t('tenant.systemModuleSettings.loadError')}
        </p>
      </main>
    );
  }

  const { module: mod, fields } = detailQuery.data;

  return (
    <main className="min-h-full bg-bg-secondary" data-testid="system-module-settings-page">
      {/* Header z nawigacją wstecz + meta modułu */}
      <header className="border-b border-border-primary bg-bg-primary px-6 py-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/system-modules')}
            aria-label={t('common.back')}
            className="rounded-md p-1 text-text-muted hover:bg-bg-tertiary"
            data-testid="system-module-settings-back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div
            className="flex h-10 w-10 items-center justify-center rounded-lg"
            style={{ backgroundColor: `${mod.color}20`, color: mod.color }}
            aria-hidden="true"
          >
            {renderIconByName(mod.icon, 'h-5 w-5')}
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-xl font-bold text-text-primary">
              {t('tenant.systemModuleSettings.title', { name: mod.name })}
            </h1>
            <p className="text-xs text-text-tertiary">
              {t('tenant.systemModuleSettings.subtitle', { entity: mod.entityTableName })}
            </p>
          </div>
        </div>
      </header>

      {/* Tab bar */}
      <div className="overflow-x-auto border-b border-border-primary bg-bg-primary">
        <nav
          className="flex min-w-max"
          aria-label={t('tenant.systemModuleSettings.tabsAriaLabel')}
          data-testid="system-module-settings-tabs"
        >
          {tabs.map((tab) => {
            const isActive = tab.id === activeTab;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setTab(tab.id)}
                className={
                  '-mb-px flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm transition-colors ' +
                  (isActive
                    ? 'border-accent-500 font-medium text-accent-600'
                    : 'border-transparent text-text-secondary hover:text-text-primary')
                }
                data-testid={`system-module-settings-tab-${tab.id}`}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {t(tab.labelKey)}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      <div className="p-6" data-testid="system-module-settings-content">
        {activeTab === 'fields' && <SystemModuleFieldsPanel moduleSlug={slug} fields={fields} />}

        {activeTab === 'layout' && (
          // F12.M Etap E (PR 2 pkt 1) — inline builder w tab Formularz (jak SellCall hub-screen).
          // Wcześniej był placeholder z buttonem „Otwórz builder formularza" → redirect do
          // osobnej strony `/system-modules/:slug/builder`. Teraz builder renderuje się
          // bezpośrednio w tab, redirect zostawiony jako alias dla deep-linków.
          <SystemModuleBuilderPage embeddedMode />
        )}

        {activeTab === 'list' && (
          // Kreator widoku listy osadzony inline (jak Formularz) — domknięcie luki
          // refactoru: formularz I widok listy edytowalne z ustawień kartoteki.
          // Standalone route `/system-modules/:slug/kreator-listy` zostaje jako alias.
          <SystemModuleListBuilderPage embeddedMode />
        )}

        {activeTab === 'numbering' && (
          <TabPlaceholder
            title={t('tenant.systemModuleSettings.tabs.numbering')}
            description={t('tenant.systemModuleSettings.placeholders.numbering.description')}
            phase="F12.I"
            icon={Hash}
          />
        )}
      </div>
    </main>
  );
}
